import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  classificationOrFallback,
  getBrief,
  partnerBriefStatusLabel,
  reassessBrief,
  type BriefDetail,
  type Stage1Classification,
} from '../../lib/partner-briefs-api';
import { StaffActionZone } from './StaffActionZone';
import { DetailPageHeader } from '../../components/DetailPageHeader';
import {
  DetailPageNavigation,
  type QueueContext,
} from '../../components/DetailPageNavigation';
import { useToast } from '../../components/ToastProvider';
import { ScoreBreakdownTable } from './ScoreBreakdownTable';
import { EvidenceDossierPanel } from './EvidenceDossierPanel';
import { AgentTracePanel } from './AgentTracePanel';
import { useBriefArtefacts } from '../../hooks/useBriefArtefacts';
import { zoneByCode, type PortalZone } from '../../lib/zones';
import { usePassthroughZone } from '../../components/ZoneContext';
import { ZoneAmbiguousNotFound } from '../../components/ZoneIndicators';
import { StaffApiError } from '../../lib/api-client';
import { InstantTime, currentTimeZone, formatDate as formatDay } from '../../lib/time-format';

/**
 * Brief detail page (Phase 5d-1, Decision 10-A).
 *
 * Surfaces the Brief metadata + Stage 1 holistic classification + Stage 2
 * 4-vector rubric + Strong Fit +20 bonus line + collapsible evidence panel
 * + collapsible agent trace + assessment history + staff action zone. The
 * layout mirrors `phase-5d-decision-10-options.html` Option 10-A.
 *
 * Evidence + agent trace are lazy (load on EXPAND not on page-load) per
 * ADR-029 § Risks "Evidence panel load time". The admin-api detail endpoint
 * does not currently issue pre-signed S3 URLs — when the agent's S3
 * artefact triad is missing (e.g. for the 5d-1 visual-UAT seed data, or
 * for any Brief produced before Phase 5c.5 D4 wires real Anthropic API
 * runs) the panel surfaces a graceful "evidence not available" message
 * with a retry affordance. Real signed-URL plumbing lands when the agent
 * starts producing artefacts in earnest — captured in the post-phase
 * doc-review notes for follow-up.
 */
export function BriefDetailPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  /**
   * ADR-122 Pattern 2 — which zone this brief lives in, carried on the link from the
   * Applications queue, exactly as `AdminCustomerDetailPage` already does for a customer.
   *
   * ⭐ ONE CALL, NOT A FAN-OUT, AND THAT IS THE POINT OF THE PATTERN. A brief exists in exactly
   * one zone; asking every zone would return one hit and N−1 404s. Absent means the home zone,
   * which is both the pre-multi-zone behaviour and the right answer for a link minted before
   * zones existed — with the ambiguity surfaced honestly if that lookup misses (below).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const zoneParam = searchParams.get('zone') ?? undefined;
  // ADR-123 — URL zone, else preferred, else home; reported to the navigation bar.
  const zone: PortalZone = usePassthroughZone(zoneParam);
  const location = useLocation();
  const queueContext = readQueueContext(location.state);
  const toast = useToast();
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; brief: BriefDetail }
    | { status: 'error'; message: string; zoneAmbiguous?: boolean }
  >({ status: 'loading' });

  // Hook must be unconditional (rules of hooks) so it stays before the
  // early returns. Pre-signed-URL plumbing for the three artefacts; Score
  // Breakdown loads eagerly on mount, Evidence Dossier + Agent Trace load
  // on collapsible expand. Passing latestS3Prefix lets the hook reset its
  // sticky `noAgentRunYet` flag when the agent finishes during auto-poll
  // (initial visit caught the Brief in pending_validation with no prefix
  // yet, the flag latched true, but auto-poll has since seen the agent
  // produce its triad — re-fire the artefact loads).
  const latestS3Prefix = state.status === 'loaded' ? state.brief.latestS3Prefix : null;
  const artefacts = useBriefArtefacts(applicationId, latestS3Prefix);

  // Counter of consecutive background-refresh failures. Surfaced as a
  // tiny banner after 3 in a row so the operator sees something is
  // wrong, but never replaces the page UI. Reset on every successful
  // refresh.
  const [refreshErrors, setRefreshErrors] = useState(0);

  // Missing-rationale detector (30 May 2026). When the score-breakdown
  // artefact lands and the agent produced NEITHER a stage1Summary nor a
  // scoringNote on a non-pending Brief, log + toast so the operator can
  // flag it during UAT. Without this the completion hero silently shows
  // an empty rationale and a real agent bug slips through unnoticed.
  // Ref-tracked so we toast once per applicationId per page session, not
  // on every 3s background poll refresh.
  const rationaleWarnedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (state.status !== 'loaded') return;
    if (artefacts.scoreBreakdown.status !== 'loaded') return;
    const b = state.brief;
    // Skip Briefs the agent hasn't processed yet (no S3 artefact prefix)
    // or that are still mid-run (no verdict to explain).
    if (!b.latestS3Prefix) return;
    if (b.status === 'pending_validation' || b.status === 'pending_reassessment') return;
    if (rationaleWarnedRef.current.has(b.applicationId)) return;
    const headline = b.stage1VerdictHeadline;
    const stage1 = artefacts.scoreBreakdown.data?.stage1Summary;
    const note = b.scoringNote;
    if (headline || stage1 || note) return;
    rationaleWarnedRef.current.add(b.applicationId);
    console.warn('BriefDetail: agent did not populate any rationale field', {
      applicationId: b.applicationId,
      status: b.status,
      latestS3Prefix: b.latestS3Prefix,
    });
    toast.push({
      variant: 'info',
      title: 'Agent rationale missing',
      detail: `Brief ${b.applicationId} (${b.status}) has no verdict headline, stage1Summary, or scoringNote. Worth flagging for the agent prompt.`,
    });
  }, [state, artefacts.scoreBreakdown, toast]);

  // Completion handoff (30 May 2026). When the status transitions from
  // pending_* to a terminal state (rejected_poor_fit, rejected_auto,
  // approved, staff_review, etc.), hold the in-progress hero with the
  // bar animated to 100% in the outcome colour and a "Continue" button
  // until the operator dismisses. Originally auto-cleared after 1.5s
  // but operator UAT 30 May 2026 — too fast to read. Now manual.
  const [completingOutcome, setCompletingOutcome] = useState<
    'approved' | 'rejected' | 'review' | null
  >(null);
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (state.status !== 'loaded') return;
    const currStatus = state.brief.status;
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = currStatus;
    if (prevStatus === null) return;
    const wasPending = prevStatus === 'pending_validation' || prevStatus === 'pending_reassessment';
    const isPending = currStatus === 'pending_validation' || currStatus === 'pending_reassessment';
    if (!wasPending || isPending) return;
    const outcome: 'approved' | 'rejected' | 'review' =
      currStatus === 'approved' || currStatus === 'pending_backlink_verification' || currStatus === 'active'
        ? 'approved'
        : currStatus === 'rejected_poor_fit' || currStatus === 'rejected_auto' || currStatus === 'rejected'
        ? 'rejected'
        : 'review';
    setCompletingOutcome(outcome);
  }, [state]);

  /**
   * Initial-load: shows the 'Loading Brief…' spinner if no Brief is
   * cached yet, otherwise quietly refreshes in the background. A poll
   * failure does NOT clobber the loaded UI — we keep the last good
   * brief and tick the consecutive-error counter.
   */
  const load = useCallback(async (opts?: { background?: boolean }) => {
    if (!applicationId) {
      setState({ status: 'error', message: 'No applicationId in URL.' });
      return;
    }
    const isBackground = opts?.background === true;
    if (!isBackground) {
      setState({ status: 'loading' });
    }
    try {
      const brief = await getBrief(applicationId, zone);
      // Tag the loaded record with the zone it was actually fetched FROM, so every action on
      // this page (approve, reject, delete, reassess) routes back to the same place rather
      // than re-deriving it. A server response never names its own zone.
      setState({ status: 'loaded', brief: { ...brief, zone: zone.code } });
      setRefreshErrors(0);
    } catch (err) {
      if (isBackground) {
        // Quiet failure — log + count, but leave the page intact. The
        // refresh banner appears at >=3 consecutive failures.
        console.warn('BriefDetail: background refresh failed', err);
        setRefreshErrors((n) => n + 1);
        return;
      }
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Could not load Brief.',
        // A 404 on a link that carried NO zone is the ambiguous case: the brief may exist in
        // another jurisdiction. A 404 on a link that DID name a zone is a genuine miss and
        // must not be dressed up as "try elsewhere" — we asked the right place.
        zoneAmbiguous: !zoneParam && err instanceof StaffApiError && err.status === 404,
      });
    }
  }, [applicationId, zone, zoneParam]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-poll while the agent is still running. Brief detail lands on the
  // page immediately after UAT submission (or a real subscriber POW
  // submission) and the agent takes ~5-15s to complete Stage 1 + Stage 2.
  // Without a poll the operator stares at `pending_validation` thinking
  // nothing happened. Stop polling as soon as the status transitions out
  // of the pending bucket.
  useEffect(() => {
    if (state.status !== 'loaded') return;
    const status = state.brief.status;
    const stillRunning = status === 'pending_validation' || status === 'pending_reassessment';
    if (!stillRunning) return;
    const id = setInterval(() => { void load({ background: true }); }, 3000);
    return () => clearInterval(id);
  }, [state, load]);

  // Browsers throttle setInterval to ~1 minute in background tabs, so
  // the 3s poll stops firing if the operator switches tabs. When they
  // come back, fire an immediate background refresh so the UI catches
  // up rather than continuing to show stale 'pending' state.
  // Caught 30 May 2026: adelaide.catholic.org.au Brief had actually
  // completed (status=rejected_poor_fit) but the operator's tab was
  // still showing 'Reassessment in progress' from 28 min earlier.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        void load({ background: true });
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [load]);

  // Safety net for the in-progress hero: if the bar has been rendered
  // for > 30s of real time (way past the 22s budget) without a status
  // change, force a refresh every 5s — the regular 3s interval is the
  // first defence but browsers throttle it in background tabs and the
  // visibilitychange listener only fires on tab return. This catches
  // the case where the operator stays on the tab but the Brief has
  // already settled and the poll missed it for some other reason.
  useEffect(() => {
    if (state.status !== 'loaded') return;
    const status = state.brief.status;
    const stillRunning = status === 'pending_validation' || status === 'pending_reassessment';
    if (!stillRunning) return;
    const t = setTimeout(() => { void load({ background: true }); }, 30000);
    return () => clearTimeout(t);
  }, [state, load]);

  if (state.status === 'loading') {
    return (
      <div className="portal-page" aria-busy="true">
        <DetailPageHeader backTo="/admin/partners" backLabel="Back to Partners" />
        <p className="portal-help">Loading Brief…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="portal-page">
        <DetailPageHeader backTo="/admin/partners" backLabel="Back to Partners" />
        {state.zoneAmbiguous ? (
          <ZoneAmbiguousNotFound
            askedZone={zone.code}
            noun="This partner application"
            // Re-running through the URL rather than local state keeps the resolved zone in
            // the address bar, so the link the operator then shares carries it.
            onTryZone={(code) => setSearchParams({ zone: code })}
          />
        ) : (
          <div className="portal-error" role="alert">{state.message}</div>
        )}
      </div>
    );
  }

  const b = state.brief;
  const classification = classificationOrFallback(b);
  // Surface the real Stage 1 summary from score-breakdown.json when present,
  // falling back to the Brief's scoringNote (older Briefs) and the
  // placeholder copy as a last resort.
  const stage1Summary =
    artefacts.scoreBreakdown.status === 'loaded'
      ? artefacts.scoreBreakdown.data?.stage1Summary ?? null
      : null;
  const strongFitBonusApplied =
    artefacts.scoreBreakdown.status === 'loaded'
      ? artefacts.scoreBreakdown.data?.strongFitBonusApplied ?? b.strongFitBonusApplied
      : b.strongFitBonusApplied;

  return (
    <div className="portal-page portal-brief-detail">
      <DetailPageHeader backTo="/admin/partners" backLabel="Back to Partners" />
      {refreshErrors >= 3 && (
        <div className="portal-brief-refresh-warning" role="status">
          ⚠ Background refresh is failing (last {refreshErrors} attempts). The page may be
          showing stale data — check your network or refresh the page.
        </div>
      )}

      <header className="portal-brief-header">
        <div className="portal-brief-header-main">
          <h1>{b.businessName}</h1>
          <div className="portal-brief-id-row">
            <span><strong>CN:</strong> <code>{b.customerNumber}</code></span>
            {b.registrationNumber && (
              <span><strong>ABN/Reg:</strong> {b.registrationNumber}</span>
            )}
            <span><strong>Website:</strong> {b.websiteUrl || '—'}</span>
            <span><strong>Submitted:</strong> {formatDate(b.submittedAt)}</span>
            {b.lastAssessedAt && (
              <span><strong>Last assessed:</strong> {formatDate(b.lastAssessedAt)}</span>
            )}
            {/* Approval timestamp — UAT 30 May 2026. reviewedAt is set
                whenever an admin Approve / Reject / Activate transition
                fired (any human decision). The approval-authority pill
                below shows WHO; this line shows WHEN. */}
            {b.reviewedAt && (
              <span><strong>Approved:</strong> {formatDate(b.reviewedAt)}</span>
            )}
          </div>
          <div className="portal-brief-status-row">
            <span className={`portal-badge portal-badge-status-${b.status}`}>{partnerBriefStatusLabel(b.status, b.briefSource, b.reviewedBy)}</span>
            {/* Approval-authority pill — UAT #23 (30 May 2026). When the
                Brief is past the approval gate, show explicitly who
                made the call. The queue row carries the same signal via
                the emoji prefix on the status pill; the detail page
                needs the full label for the audit-trail read. */}
            {(b.status === 'active'
              || b.status === 'approved'
              || b.status === 'pending_backlink_verification'
              || b.status === 'manual_add_invited'
              || b.status === 'manual_add_expired'
              || b.status === 'suspended') && (
              <span className={`portal-badge portal-badge-approval-${b.reviewedBy ? 'admin' : 'ai'}`}>
                {b.reviewedBy ? '👤 Approved by Admin' : '🤖 Approved by AI'}
              </span>
            )}
            {classification && (
              <span className={`portal-badge portal-badge-classification-${classification}`}>
                {classificationLabel(classification)} · Stage 1
              </span>
            )}
            {/* "Assessment X of 3" pill removed 30 May 2026 — reassessment
               count is no longer capped (monthly cap removed earlier; the
               per-Brief 3-attempt limit was also lifted). Operator can
               read the count from the Assessment History section if
               needed. */}
            {(b.status === 'pending_validation' || b.status === 'pending_reassessment') && (
              <span className="portal-brief-assessing" role="status">
                <span className="portal-brief-assessing-dot" aria-hidden="true" />
                Agent running — auto-refreshing every 3s
              </span>
            )}
          </div>
        </div>
        <div className="portal-brief-score-block">
          <div className="portal-brief-score-label">POW Score</div>
          <div
            className={`portal-brief-score-value portal-brief-score-${scoreBand(b.score)}${b.status === 'rejected_poor_fit' ? ' portal-brief-score-offtarget' : ''}`}
            style={{ color: b.status === 'rejected_poor_fit' ? undefined : scoreHueColor(b.score) }}
            title={b.status === 'rejected_poor_fit' ? 'Stage 1 rejected as off-target — wrong business category, not a quality measure' : undefined}
          >
            {b.status === 'rejected_poor_fit' ? '—' : (b.score ?? '—')}
          </div>
          {b.status === 'rejected_poor_fit' && (
            <div className="portal-brief-score-offtarget-label">Off-target market</div>
          )}
          {b.strongFitBonusApplied === true && (
            <div className="portal-brief-score-bonus">+20 Strong Fit bonus</div>
          )}
          <ReassessButton brief={b} onTriggered={() => void load({ background: true })} />
        </div>
      </header>

      {b.status === 'agent_failed' ? (
        <AgentFailedCard
          websiteUrl={b.websiteUrl}
          errorMessage={b.agentErrorMessage ?? null}
          errorAt={b.agentErrorAt ?? null}
          onReassess={() => void load({ background: true })}
          applicationId={b.applicationId}
          zone={zone}
        />
      ) : (b.status === 'pending_validation' || b.status === 'pending_reassessment' || completingOutcome !== null) ? (
        <AssessmentInProgress
          websiteUrl={b.websiteUrl}
          startedAt={
            b.status === 'pending_reassessment' && b.reassessRequestedAt
              ? b.reassessRequestedAt
              : b.submittedAt
          }
          status={b.status}
          applicationId={b.applicationId}
          zone={zone}
          completingOutcome={completingOutcome}
          onDismissCompletion={() => setCompletingOutcome(null)}
          completingRationale={b.stage1VerdictHeadline ?? stage1Summary ?? b.scoringNote ?? null}
        />
      ) : (
        <>
          <Stage1Section
            brief={b}
            classification={classification}
            stage1Summary={stage1Summary}
          />
          <ScoreBreakdownTable
            state={artefacts.scoreBreakdown}
            noAgentRunYet={artefacts.noAgentRunYet}
            strongFitBonusApplied={strongFitBonusApplied}
            briefScore={b.score}
          />
          <DescriptionSection brief={b} />
          <EvidenceDossierPanel
            state={artefacts.evidenceDossier}
            noAgentRunYet={artefacts.noAgentRunYet}
            onExpand={artefacts.loadEvidenceDossier}
            s3Prefix={b.latestS3Prefix}
            briefStatus={b.status}
          />
          <AgentTracePanel
            state={artefacts.agentTrace}
            noAgentRunYet={artefacts.noAgentRunYet}
            onExpand={artefacts.loadAgentTrace}
            lastAssessedAt={b.lastAssessedAt}
          />
          <AssessmentHistorySection brief={b} />
        </>
      )}

      <StaffActionZone brief={b} onBriefUpdated={() => void load({ background: true })} />

      {/* Danger Zone removed 30 May 2026 — Delete folded into Staff Actions
          (super_admin gated there). */}

      <DetailPageNavigation context={queueContext} backTo="/admin/partners" />
    </div>
  );
}

/**
 * Type-safe read of the queue context that ApplicationsQueue +
 * ActivePartnersTable pass via React Router's location.state. Returns null
 * if the page was opened directly (deep link, refresh, back button into
 * fresh history) — Prev/Next disappear cleanly in that case.
 */
function readQueueContext(state: unknown): QueueContext | null {
  if (typeof state !== 'object' || state === null) return null;
  const wrapper = state as Record<string, unknown>;
  const ctx = wrapper['queueContext'];
  if (typeof ctx !== 'object' || ctx === null) return null;
  const candidate = ctx as Record<string, unknown>;
  const queue = candidate['queue'];
  const currentIdx = candidate['currentIdx'];
  const basePath = candidate['basePath'];
  const backTo = candidate['backTo'];
  if (
    !Array.isArray(queue) ||
    !queue.every((id) => typeof id === 'string') ||
    typeof currentIdx !== 'number' ||
    typeof basePath !== 'string' ||
    typeof backTo !== 'string'
  ) {
    return null;
  }
  return { queue: queue as string[], currentIdx, basePath, backTo };
}

// =============================================================================
// Assessment-in-progress hero panel (Phase 5d-1 follow-up)
//
// Replaces the empty Stage 1 / Vector / Description / Evidence sections
// while the Brief is in pending_validation or pending_reassessment so
// the operator sees clear feedback ("yes the agent is running") instead
// of placeholder copy that reads like "no data found".
//
// Auto-poll on the parent BriefDetailPage still fires every 3s and
// flips state when the agent lands, at which point this panel is
// replaced by the real Stage 1 + Vector breakdown + Description +
// collapsibles.
// =============================================================================

interface AssessmentInProgressProps {
  websiteUrl: string;
  /** Wall-clock start for the progress bar. For an initial assessment
   *  this is Brief.submittedAt; for a reassess the caller passes
   *  Brief.reassessRequestedAt so the bar restarts from 0/red instead
   *  of continuing the original submission clock (operator hit
   *  Reassess on an old Brief 29 May 2026 and the bar jumped to
   *  step 6 / 100% / 22m elapsed immediately). */
  startedAt: string;
  status: string;
  applicationId: string;
  /** ADR-122 Pattern 2 — the brief's own zone, so Force-reassess targets it. */
  zone: PortalZone;
  /** When set, the parent has detected the status flipped from pending
   *  to terminal and the bar animates to 100% in the outcome colour.
   *  The hero holds until the operator clicks Continue (was a 1.5s
   *  auto-dismiss originally — too fast to read per UAT 30 May 2026). */
  completingOutcome?: 'approved' | 'rejected' | 'review' | null;
  /** Called when the operator dismisses the completion hero. Parent
   *  clears completingOutcome which unmounts AssessmentInProgress and
   *  renders the final view. */
  onDismissCompletion?: () => void;
  /** Short agent-authored rationale to render under the recommendation
   *  label when completing. Parent passes stage1Summary (from
   *  score-breakdown.json) for Stage 1 verdicts, or scoringNote (from
   *  the Brief) for Stage 2 uncertainty messages. Empty/null hides the
   *  line gracefully. */
  completingRationale?: string | null;
}

// Typical agent runtime broken into 6 visual steps. Total budget is
// ~22s which sits inside the documented 15-30s range. These are
// time-based estimates — the agent doesn't stream intermediate state,
// so we map elapsed → current step purely from cumulative durations.
// Portfolio is the heaviest step (most tool calls); matchQuality is
// pure logic so it gets the shortest slice.
const PROGRESS_STAGES: Array<{ label: string; durationMs: number }> = [
  { label: 'Stage 1 — holistic classification',         durationMs: 4000  },
  { label: 'Portfolio authenticity — verifying clients', durationMs: 7000 },
  { label: 'Match quality — CN + token validation',      durationMs: 2000 },
  // ⚠ NOT "ABN / WHOIS". Corrected 17 September 2026 with the register wiring: this
  // stage is the identityAuthenticity VECTOR, and the label named the wrong things on
  // both counts. "ABN" is the AU identifier only — an NZ or US applicant watched the
  // portal say their ABN was being looked up. And WHOIS is not part of this vector at
  // all: `whois_lookup` feeds the portfolio longevity bonus. What actually runs here is
  // `verify_business_registration` (ABR / ACNC / NZBN / OpenSOSData) plus the
  // phone-on-website and address-on-website checks. Keep this jurisdiction-neutral —
  // the applicant watching it may be in any of the three.
  { label: 'Identity authenticity — business register + web signals', durationMs: 5000 },
  { label: 'Web presence breadth — LinkedIn + search',   durationMs: 3000 },
  { label: 'Finalising score + decision',                durationMs: 1000 },
];
const PROGRESS_TOTAL_MS = PROGRESS_STAGES.reduce((s, x) => s + x.durationMs, 0);

/**
 * Trim a long agent rationale to a short sentence so it fits cleanly on
 * the completion hero. Prefer the first sentence (period/!/? bounded) if
 * it's ≤ MAX_WORDS; otherwise truncate to MAX_WORDS + ellipsis. The full
 * rationale stays visible on the Brief detail page underneath.
 *
 * 30 May 2026 — operator UAT wanted a short verdict line, not a paragraph.
 * A follow-up agent-prompt change should emit a one-sentence verdict
 * alongside the long Stage 1 summary; until then truncating client-side
 * is the pragmatic call.
 */
function shortenRationale(text: string, maxWords: number = 15): string {
  const cleaned = text.trim();
  if (!cleaned) return cleaned;
  const firstSentence = cleaned.match(/^[^.!?]+[.!?]/)?.[0]?.trim();
  if (firstSentence) {
    const wordCount = firstSentence.split(/\s+/).length;
    if (wordCount <= maxWords) return firstSentence;
  }
  const words = cleaned.split(/\s+/);
  if (words.length <= maxWords) return cleaned;
  return words.slice(0, maxWords).join(' ') + '…';
}

function progressFromElapsed(elapsedMs: number): { stepIdx: number; pct: number } {
  const cappedElapsed = Math.min(elapsedMs, PROGRESS_TOTAL_MS);
  let acc = 0;
  let stepIdx = PROGRESS_STAGES.length - 1;
  for (let i = 0; i < PROGRESS_STAGES.length; i++) {
    acc += PROGRESS_STAGES[i]!.durationMs;
    if (cappedElapsed < acc) { stepIdx = i; break; }
  }
  // Asymptotic curve — bar never sits still at 95% when the agent runs
  // long (real runs often hit 45-90s due to Anthropic latency + tool
  // calls). Operator UAT 30 May 2026: a frozen-at-95% bar reads as
  // hung even when the elapsed clock is still ticking. Below the typical
  // 22s budget the bar tracks linearly to 80%; beyond that it follows an
  // exponential approach to a 99% ceiling, so it always looks alive.
  // Real 100% only lands when the parent flips completingOutcome.
  const LINEAR_TARGET_PCT = 80; // where we are at PROGRESS_TOTAL_MS
  const ASYMPTOTE_PCT = 99;
  const TAIL_HALFLIFE_MS = 30000; // every 30s of overshoot halves the gap to 99
  let pct: number;
  if (elapsedMs <= PROGRESS_TOTAL_MS) {
    pct = (elapsedMs / PROGRESS_TOTAL_MS) * LINEAR_TARGET_PCT;
  } else {
    const overshoot = elapsedMs - PROGRESS_TOTAL_MS;
    const remaining = ASYMPTOTE_PCT - LINEAR_TARGET_PCT;
    pct = ASYMPTOTE_PCT - remaining * Math.pow(0.5, overshoot / TAIL_HALFLIFE_MS);
  }
  return { stepIdx, pct: Math.round(pct) };
}

function AssessmentInProgress({ websiteUrl, startedAt, status, applicationId, zone, completingOutcome, onDismissCompletion, completingRationale }: AssessmentInProgressProps) {
  // Tick every 500ms so the bar + step animate smoothly.
  const [, setTick] = useState(0);
  const [forceBusy, setForceBusy] = useState(false);
  const [forceErr, setForceErr] = useState<string | null>(null);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);

  const elapsedMs = Math.max(0, Date.now() - Date.parse(startedAt));
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const elapsed = elapsedSec < 60
    ? `${elapsedSec}s`
    : `${Math.floor(elapsedSec / 60)}m ${elapsedSec % 60}s`;
  const isReassess = status === 'pending_reassessment';
  // After PROGRESS_TOTAL_MS (22s) the bar's into the asymptotic tail —
  // soften the messaging so the operator knows the agent is still
  // working even though the bar appears to crawl. After 90s show a
  // sharper warning, and at 120s the server allows force-reassess.
  const isStillWorking = elapsedMs > PROGRESS_TOTAL_MS;
  const isLate = elapsedSec >= 90;
  const isOrphan = elapsedSec >= 120;

  const baseProgress = progressFromElapsed(elapsedMs);
  const totalSteps = PROGRESS_STAGES.length;
  // Completion handoff overrides — when the parent has detected the
  // status flipped to terminal, snap the bar to 100% and label the
  // outcome instead of continuing the elapsed-time animation.
  const isCompleting = completingOutcome != null;
  const stepIdx = isCompleting ? totalSteps - 1 : baseProgress.stepIdx;
  const pct = isCompleting ? 100 : baseProgress.pct;
  // Operator UAT 30 May 2026: "Decision" overstates the agent's role —
  // it's a recommendation, the operator owns the Approve / Reject call.
  // Capitalised verb so it reads as a label, not a sentence fragment.
  const currentStep = isCompleting
    ? {
        label:
          completingOutcome === 'approved'
            ? 'Recommendation: Approve'
            : completingOutcome === 'rejected'
              ? 'Recommendation: Reject'
              : 'Recommendation: Human Review',
        durationMs: 0,
      }
    : PROGRESS_STAGES[baseProgress.stepIdx]!;

  async function forceReassess() {
    setForceBusy(true);
    setForceErr(null);
    try {
      await reassessBrief(applicationId, zone);
      // Page auto-poll will pick up the new status.
    } catch (e) {
      setForceErr(e instanceof Error ? e.message : 'Reassess failed.');
    } finally {
      setForceBusy(false);
    }
  }

  return (
    <section className="portal-assessment-progress" aria-live="polite">
      <h2>
        {isCompleting
          ? 'Assessment Complete'
          : isReassess
            ? 'Reassessment in progress'
            : 'Assessment in progress'}
      </h2>
      <p className="portal-assessment-progress-url">
        {isCompleting ? 'POW Suitability Agent finished against' : 'Running the POW Suitability Agent against'}{' '}
        <code>{websiteUrl || '—'}</code>
      </p>
      <div
        className="portal-assessment-progress-step"
        aria-live="polite"
      >
        <span className="portal-assessment-progress-step-counter">
          Step {Math.min(stepIdx + 1, totalSteps)} of {totalSteps}
        </span>
        <span
          className={`portal-assessment-progress-step-label${isCompleting && completingOutcome ? ' portal-assessment-progress-step-label-' + completingOutcome : ''}`}
        >
          {isLate && !isCompleting ? 'Finalising…' : currentStep.label}
        </span>
        {isCompleting && completingRationale && (
          <p
            className="portal-assessment-progress-rationale"
            title={completingRationale.length > 120 ? completingRationale : undefined}
          >
            “{shortenRationale(completingRationale)}”
          </p>
        )}
      </div>
      <div
        className={`portal-assessment-progress-bar${isCompleting ? ' portal-assessment-progress-bar-' + completingOutcome : ''}`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={isCompleting ? `Assessment complete: ${completingOutcome}` : `Estimated agent progress: ${pct}%`}
        style={{ ['--progress' as string]: `${pct}%` }}
      >
        {/* Fixed red→green gradient spans the FULL bar width. The fill
           layer reveals it from the left via clip-path keyed off the
           --progress CSS variable, so the leading edge cycles through
           the spectrum as the bar grows. Operator framing 30 May 2026:
           'progression towards a pass' — at 25% you see red→orange, at
           75% you see red→yellow→lime, at 100% the full red→green. */}
        <div className="portal-assessment-progress-bar-fill" />
      </div>
      {isCompleting ? (
        <div className="portal-assessment-progress-complete-actions">
          <button
            type="button"
            className="portal-assessment-progress-continue"
            onClick={onDismissCompletion}
            /* NOT a page-load autofocus. completingOutcome starts null and
               its effect returns early while prevStatus is null, so it can
               never be set on first render — it only flips while the
               operator is already watching this panel poll through a
               20–60s run. This button mounts at that transition as the sole
               action of the completed state, inside an aria-live="polite"
               section that announces the outcome. Moving focus to it is the
               async-completion focus-management pattern. */
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            Continue to result →
          </button>
        </div>
      ) : (
        <>
          <div className={`portal-assessment-progress-meta portal-assessment-progress-meta-${elapsedSec < 15 ? 'fresh' : elapsedSec < 30 ? 'ontime' : elapsedSec < 60 ? 'warm' : elapsedSec < 90 ? 'late' : 'overdue'}`}>
            <strong>{elapsed}</strong> elapsed · typical run takes 20–60s
          </div>
          {isStillWorking && !isLate && (
            <div className="portal-assessment-progress-stillworking">
              <span className="portal-assessment-progress-stillworking-pulse" aria-hidden="true" />
              Still working — Anthropic responses + tool calls can take 30–90s during peak load.
            </div>
          )}
          {isLate && (
            <div className="portal-assessment-progress-late">
              ⚠ Taking longer than typical. The agent may have hit an Anthropic API
              outage or a tool timeout.
              {isOrphan && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="portal-link-button-secondary"
                    onClick={() => void forceReassess()}
                    disabled={forceBusy}
                  >
                    {forceBusy ? 'Re-firing…' : 'Force reassess'}
                  </button>
                </>
              )}
              {forceErr && <div className="portal-error">{forceErr}</div>}
            </div>
          )}
          <p className="portal-assessment-progress-footnote">
            Progress estimated from typical per-stage durations — the agent doesn't
            stream intermediate state. Auto-refresh every 3s.
          </p>
        </>
      )}
    </section>
  );
}

// =============================================================================
// Agent-failed card — replaces the in-progress panel when the agent
// gave up (e.g. Anthropic 5xx after retries). Shows the error message
// + a single Reassess CTA so the operator can clear it with one click.
// =============================================================================

interface AgentFailedCardProps {
  applicationId: string;
  /** ADR-122 Pattern 2 — the brief's own zone, so Retry targets it. */
  zone: PortalZone;
  websiteUrl: string;
  errorMessage: string | null;
  errorAt: string | null;
  onReassess: () => void;
}

function AgentFailedCard({
  applicationId,
  zone,
  websiteUrl,
  errorMessage,
  errorAt,
  onReassess,
}: AgentFailedCardProps) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function retry() {
    setBusy(true);
    setErr(null);
    try {
      await reassessBrief(applicationId, zone);
      onReassess();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Reassess failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="portal-agent-failed-card" aria-live="polite">
      <div className="portal-agent-failed-icon" aria-hidden="true">⚠</div>
      <h2>Agent failed to complete the assessment</h2>
      <p className="portal-agent-failed-url">
        Target: <code>{websiteUrl || '—'}</code>
      </p>
      {errorMessage && (
        <pre className="portal-agent-failed-message" aria-label="Error detail">
          {errorMessage}
        </pre>
      )}
      {errorAt && (
        <p className="portal-agent-failed-when">Failed at <InstantTime iso={errorAt} /></p>
      )}
      <p className="portal-agent-failed-help">
        Often this is a transient Anthropic API outage (5xx). The agent now
        retries 5xx + 429 with backoff before giving up — if you see this card,
        the API was unavailable for the entire retry window. Click Reassess
        to try again.
      </p>
      <button
        type="button"
        className="portal-action-btn portal-action-btn-reactivate"
        onClick={() => void retry()}
        disabled={busy}
      >
        {busy ? 'Reassessing…' : '↻ Reassess now'}
      </button>
      {err && <div className="portal-error" role="alert">{err}</div>}
    </section>
  );
}

// =============================================================================
// Reassess button — re-run pow-agent on demand
// =============================================================================

interface ReassessButtonProps {
  brief: BriefDetail;
  onTriggered: () => void;
}

function ReassessButton({ brief, onTriggered }: ReassessButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stillRunning =
    brief.status === 'pending_validation' || brief.status === 'pending_reassessment';

  async function onClick() {
    if (busy || stillRunning) return;
    setError(null);
    setBusy(true);
    try {
      await reassessBrief(brief.applicationId, zoneByCode(brief.zone));
      onTriggered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reassess failed.');
    } finally {
      setBusy(false);
    }
  }

  const spinning = busy || stillRunning;
  return (
    <div className="portal-brief-reassess">
      <button
        type="button"
        className={`portal-link-button-secondary portal-brief-reassess-btn${spinning ? ' portal-reassess-busy' : ''}`}
        onClick={() => void onClick()}
        disabled={busy || stillRunning}
        title={
          stillRunning
            ? 'Reassess in progress…'
            : 'Reassess application — re-runs the POW agent against the current website + business data.'
        }
        aria-label="Reassess application"
      >
        <span className="portal-reassess-glyph" aria-hidden="true">↻</span>
        {' '}
        {busy ? 'Starting…' : stillRunning ? 'Running…' : 'Reassess'}
      </button>
      {error && <div className="portal-error portal-brief-reassess-error">{error}</div>}
    </div>
  );
}

// =============================================================================
// Danger zone — super_admin soft-delete (Phase 5d-1 follow-up)
// =============================================================================

// Sections
// =============================================================================

function Stage1Section({
  brief,
  classification,
  stage1Summary,
}: {
  brief: BriefDetail;
  classification: Stage1Classification | null;
  stage1Summary: string | null;
}) {
  if (!classification) return null;
  // Summary preference: real Stage 1 from score-breakdown.json → fallback to
  // the Brief's scoringNote (older Briefs) → structural placeholder.
  const summary =
    stage1Summary ||
    brief.scoringNote ||
    'Stage 1 personalised summary will appear here once the agent has run against this Brief. The summary is sourced from score-breakdown.json.stage1Summary per ADR-031 §1.';
  return (
    <section className="portal-brief-section">
      <div className="portal-brief-section-header">
        <h2 className="portal-brief-section-header-label">Stage 1 holistic assessment</h2>
      </div>
      <div className="portal-brief-stage1">
        <span className={`portal-badge portal-badge-classification-${classification}`}>
          {classificationLabel(classification)}
        </span>
        <p>{summary}</p>
      </div>
    </section>
  );
}

function DescriptionSection({ brief }: { brief: BriefDetail }) {
  return (
    <section className="portal-brief-section">
      <h2>Description of practice (applicant-supplied)</h2>
      <p className="portal-brief-description">{brief.descriptionOfPractice || '—'}</p>
    </section>
  );
}

// VectorBreakdownSection, CollapsibleEvidenceSection, and
// CollapsibleAgentTraceSection are now dedicated components —
// ScoreBreakdownTable, EvidenceDossierPanel, AgentTracePanel — wired to
// the useBriefArtefacts lazy-loader hook.

function AssessmentHistorySection({ brief }: { brief: BriefDetail }) {
  return (
    <section className="portal-brief-section">
      <h2>Assessment history</h2>
      {brief.assessmentCount === 0 ? (
        <p className="portal-help">No assessments yet — the agent has not run against this Brief.</p>
      ) : (
        <ul className="portal-brief-history">
          <li>
            <strong>Run v{brief.assessmentCount}</strong> · {formatDate(brief.lastAssessedAt ?? brief.submittedAt)} · Score{' '}
            <code>{brief.score ?? '—'}</code>
            {brief.scoringNote && (
              <em className="portal-brief-history-note"> · {brief.scoringNote}</em>
            )}
          </li>
        </ul>
      )}
      {brief.assessmentCount > 1 && (
        <p className="portal-help portal-brief-help-note">
          Prior-run rows (v1 … v{brief.assessmentCount - 1}) populate from the agent-trace S3 prefix.
          Surfaced as part of the same lazy-loading plumbing as the evidence panel.
        </p>
      )}
    </section>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function classificationLabel(c: Stage1Classification): string {
  switch (c) {
    case 'strong_fit':
      return 'Strong Fit';
    case 'possible_fit':
      return 'Possible Fit';
    case 'poor_fit':
      return 'Poor Fit';
  }
}

function formatDate(iso: string | null): string {
  return formatDay(iso, 'medium', currentTimeZone(), iso || '—');
}

function scoreBand(score: number | null): 'high' | 'medium' | 'low' | 'none' {
  if (score == null) return 'none';
  if (score >= 75) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

/**
 * scoreHueColor — POW Score number colour interpolated from red (0)
 * through orange / yellow / lime to green (100). Pure score-based, no
 * time component. Score 0 = pure red, 50 = yellow, 100 = green.
 * Returns undefined for null score so callers can fall back to default.
 */
export function scoreHueColor(score: number | null): string | undefined {
  if (score == null) return undefined;
  const clamped = Math.max(0, Math.min(100, score));
  const hue = Math.round((clamped / 100) * 120);
  return `hsl(${hue}, 80%, 56%)`;
}
